import { Settings, Brain, GripHorizontal } from 'lucide-react';
import { useRef, useState } from 'react';


import { useAI } from '../../context/AIContext';
import { userData } from '../../data/mockDatabase';

const AutoWidgetWrapper = ({ 
  children, 
  title,
  onSettingsClick,
}) => {
  const contentRef = useRef(null); // Reference to widget content
  const { openAI } = useAI(); // AI context to trigger AI functions
  const { currentUser } = userData; // Mocked user data for metadata
  const [widgetInfo, setWidgetInfo] = useState(null); // Optionally store widget data
  const [isInfoVisible, setIsInfoVisible] = useState(false); // Track if widget info is visible

  // Function to scrape widget data on AI trigger
  const scrapeWidgetData = () => {
    const element = contentRef.current;
    if (!element) return null;

    // Find all elements with data attributes
    const dataMap = {};
    // First, get all elements in the widget
    const allElements = element.querySelectorAll('*');
    allElements.forEach(el => {
      // Check if the element has any data attributes
      if (el.attributes) {
        Array.from(el.attributes).forEach(attr => {
          if (attr.name.startsWith('data-')) {
            const key = attr.name.replace('data-', '');
            try {
              dataMap[key] = JSON.parse(attr.value); // Parse JSON if possible
            } catch {
              dataMap[key] = attr.value; // Fallback to raw data if JSON parsing fails
            }
          }
        });
      }
    });

    // Clean text content excluding scripts and styles
    const getCleanText = (el) => {
      const clone = el.cloneNode(true);
      const scripts = clone.querySelectorAll('script, style');
      scripts.forEach(script => script.remove());
      return clone.textContent || clone.innerText || '';
    };

    // Count visible elements in the widget
    const visibleElements = element.querySelectorAll('*:not(script):not(style)');
    
    // Extract table data if present
    const tables = Array.from(element.querySelectorAll('table')).map(table => {
      return {
        headers: Array.from(table.querySelectorAll('th')).map(th => th.textContent),
        rows: Array.from(table.querySelectorAll('tr')).map(tr => 
          Array.from(tr.querySelectorAll('td')).map(td => td.textContent)
        )
      };
    });

    // Extract lists if present
    const lists = Array.from(element.querySelectorAll('ul, ol')).map(list => 
      Array.from(list.querySelectorAll('li')).map(li => li.textContent)
    );

    // Assemble all the scraped data
    return {
      widgetTitle: title,
      timestamp: new Date().toISOString(),
      content: {
        text: getCleanText(element),
        structuredData: dataMap,
        tables,
        lists,
        metrics: {
          elementCount: visibleElements.length,
          textLength: getCleanText(element).length,
          hasTable: tables.length > 0,
          hasLists: lists.length > 0
        }
      },
      metadata: {
        user: {
          id: currentUser.id,
          username: currentUser.username,
          role: currentUser.role,
          department: currentUser.department
        },
        capture: {
          method: 'auto-scrape',
          version: '1.0',
          timestamp: new Date().toISOString()
        }
      }
    };
  };

  // Handle AI button click
  const handleAIClick = (e) => {
    e.stopPropagation();
    const widgetData = scrapeWidgetData(); // Scrape widget data on click
    if (widgetData) {
      setWidgetInfo(widgetData); // Store the widget data
      setIsInfoVisible(!isInfoVisible); // Toggle info visibility
      if (!isInfoVisible) {
        openAI(widgetData); // Only trigger AI interaction when opening
      }
    }
  };

  return (
    <div className="h-full flex flex-col rounded-lg overflow-hidden shadow-sm bg-[var(--color-highlight)]">
      <div className="flex-shrink-0 flex items-center justify-between p-3 bg-[var(--color-primary)]">
        <h3 className="text-lg font-semibold text-white">{title}</h3>
        <div className="flex items-center space-x-2">
          {/* AI button to trigger scraping and AI */}
          <button 
            onClick={handleAIClick}
            className="p-1 hover:bg-opacity-80 rounded transition-colors duration-200 text-white"
            title="Ask AI about this widget"
          >
            <Brain className="h-5 w-5" />
          </button>
          
          {/* Settings button */}
          <button 
            onClick={onSettingsClick}
            className="p-1 hover:bg-opacity-80 rounded transition-colors duration-200 text-white"
            title="Widget settings"
          >
            <Settings className="h-5 w-5" />
          </button>
          
          {/* Dragging handle */}
          <div 
            className="cursor-move text-white" 
            title="Drag to move"
          >
            <GripHorizontal className="h-5 w-5" />
          </div>
        </div>
      </div>
      
      <div className="flex-1 overflow-auto bg-[var(--color-widgetBackground)]" ref={contentRef}>
        {children} {/* Render widget content */}
      </div>

      {/* Display the scraped widget data when toggled */}
      {widgetInfo && isInfoVisible && (
        <div 
          className="widget-info-region p-2 bg-gray-100 border-t border-gray-200 max-h-60 overflow-auto"
          role="region"
          aria-live="polite" 
          tabIndex={-1}
        >
          <div onClick={(e) => e.stopPropagation()} className="widget-info-content">
            <div className="flex justify-between items-center mb-2">
              <strong className="text-sm">Widget Data</strong>
              <button 
                onClick={() => setIsInfoVisible(false)} 
                className="text-xs text-gray-600 hover:text-gray-800"
              >
                Close
              </button>
            </div>
            <pre className="text-xs whitespace-pre-wrap break-all">
              {JSON.stringify(widgetInfo, null, 2)}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
};

export default AutoWidgetWrapper;
